import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getStats } from '../../../api';

const StatCard = ({ icon, label, value, to, loading }) => {
  const inner = (
    <div className={`flex items-center gap-4 bg-gray-50 border border-gray-100 rounded-xl p-5 transition-all
      ${to ? 'hover:border-red-1 hover:shadow-md cursor-pointer' : ''}`}>
      <div className='flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-1 shrink-0'>
        {icon}
      </div>
      <div className='min-w-0'>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide truncate'>{label}</p>
        {loading ? (
          <div className='h-7 w-20 mt-1 rounded bg-gray-200 animate-pulse' />
        ) : (
          <p className='text-2xl font-bold text-gray-800'>{value?.toLocaleString() ?? '—'}</p>
        )}
      </div>
    </div>
  );

  return to ? <Link to={to}>{inner}</Link> : inner;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStats()
      .then((res) => setStats(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className='font-montserrat w-full'>
      <div className='flex items-center justify-between pb-1 mb-5 border-b-2'>
        <h1 className='font-semibold text-xl'>Dashboard</h1>
        {!loading && (
          <span className='text-xs text-gray-400'>
            Actualizado ahora
          </span>
        )}
      </div>

      {/* Primary stats */}
      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4'>
        <StatCard
          loading={loading}
          label='Usuarios registrados'
          value={stats?.users}
          to='/administracion/usuarios'
          icon={
            <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' />
            </svg>
          }
        />
        <StatCard
          loading={loading}
          label='Usuarios en línea'
          value={stats?.loggedInUsers}
          icon={
            <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M9 12a3 3 0 116 0 3 3 0 01-6 0z' />
            </svg>
          }
        />
        <StatCard
          loading={loading}
          label='Capítulos'
          value={stats?.chapters}
          icon={
            <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
              <path strokeLinecap='round' strokeLinejoin='round' d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          }
        />
        <StatCard
          loading={loading}
          label='Series'
          value={stats?.series}
          to='/administracion/series'
          icon={
            <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
            </svg>
          }
        />
        <StatCard
          loading={loading}
          label='Películas'
          value={stats?.movies}
          to='/administracion/peliculas'
          icon={
            <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z' />
            </svg>
          }
        />
      </div>

      {/* Quick links */}
      <div className='mt-6 border-t-2 pt-4'>
        <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3'>Accesos rápidos</h2>
        <div className='flex flex-wrap gap-2'>
          {[
            { label: 'Usuarios',  to: '/administracion/usuarios' },
            { label: 'Series',    to: '/administracion/series' },
            { label: 'Películas', to: '/administracion/peliculas' },
            { label: 'Géneros',   to: '/administracion/generos' },
            { label: 'Videos',    to: '/administracion/videos' },
            { label: 'Correos',   to: '/administracion/correos' },
            { label: 'Campañas',  to: '/administracion/correos/campanas' },
          ].map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              className='px-4 py-1.5 rounded-full border border-gray-200 text-sm font-semibold text-gray-600 hover:border-red-1 hover:text-red-1 transition-colors bg-white'
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
